// crowns: the CanopyGen model shared by the GPU pass (canopygen.js) and its
// CPU REFERENCE (APP_FORESTVOL §3, §6; APP_GENSURF §2 "every GPU pass has a
// CPU reference"). Pure (no THREE, no DOM): scripts/check-forest.mjs runs the
// reference on fixtures, __vr.genCheck() diffs a GPU tile against it. The
// shaders in canopygen.js port splatRef / shadeRef line for line — change
// both or neither.
//
// A canopy tile of level c: 256² texels of 2·2^c m (tile_m 512·2^c = the
// ground tree's node level c + 2). The WORK FRAME is the tile + HALO texels
// on every side (288²): the drawn ground (the resident ground-tree nodes) and
// every crown whose disc reaches the frame are splatted into it, so a tile
// and its neighbour see the same crowns, the same ground under a crown's
// centre and the same occluders in their light — no seam.
//
// The crown is the bake's DRAWN crown (bake.py DrawnCasters._tree_discs, the
// casters the ground's light was baked with): drawn height s = h ×
// FORM_CFG.hScale (an unmeasured stand, r 0: × the treeline ramp of its ground
// height), width factor from the measured r (2.35 r / s, clipped 0.28..1.15)
// else FORM_CFG.w, drawn radius 0.5 × SILHOUETTE × s × wf, the top profile
// CROWN_PROFILE (cone: linear from the tip to the crown base at the radius;
// dome: the upper half ellipsoid, the rim at mid-crown). Species: the
// world's speciesLut on the appear byte; 255 (no ortho reading) = the main
// species (the bake's caster rule). Season (C10, season.js formSeason at the
// tile's doy; a species' own ramp from the world's SEA1, season.js seaRamp): the species tint, and `bare` shrinks the crown radius toward
// the branch skeleton (BARE_R) and mixes its colour toward BARK.
//
// Heights are tile-relative: rel = y − y0 (y0 the tile's floor, SPAN_M the
// span the depth / R16UI codes hold). H = round(rel × H_Q) (1/32 m), 0 = no
// canopy (ground wins the texel), clamped to 1..65535.
//
// THE SHRUB FAMILY (gs-family-shrub, APP_FORESTVOL §4e): the same pass over
// the shrub crown packs (CPK1 kind 1, 512 m) at shrub scale — levels 0..1
// (1 / 2 m texels, 256 / 512 m tiles), the same frame / H / A / B layout. A
// shrub is the bake's drawn shrub caster (bake.py DrawnCasters._shrub_discs,
// LIGHT_SHRUB / LIGHT_SHRUB_R): a dome from the ground, top h = min(byte / 50,
// SHRUB_TOP_M), radius SHRUB_R × h; none on ground above the world's shrub cap
// (shrubs.js / grass.js shrubCapM). Its "species" is the shrub STYLE: leafy 0
// | needle 1 by the per-shrub hash and needle_share (shrubNeedle, the near
// shrubs' rule), coloured from meta.vegetation.shrubs (shrubStyle); no season
// (no tint, no bare), snow-catch from the upward normal only (no crown-depth
// term). Light: shadeRef with SHRUB_LIGHT (a shorter sun march, closer
// openness taps). FAMILY.tree / FAMILY.shrub carry the per-family numbers the
// pass (canopygen.js) and this reference share.
import { FORM_CFG, resolveSpecies, speciesLut } from './vegpaint.js';
import { formSeason, seaRamp } from './season.js';
import { CPK_H_PER_M, CPK_HEADER, readCrownHeader, crownHash } from './crownpack.js';

export const CANOPY_N = 256;              // texels per tile side (gensched CANOPY_TEXELS)
export const HALO = 16;                   // work-frame texels beyond the tile on each side
export const FRAME_N = CANOPY_N + 2 * HALO;
export const CANOPY_MIPS = Math.log2(CANOPY_N) + 1;
export const H_Q = 32;                    // H quantum: 1/32 m
export const SPAN_M = 2047;               // the height span above y0 (65535 / 32 = 2047.97)
export const REACH_M = 16;                // the widest drawn crown radius (0.425 × 31.875 × 1.15 = 15.6 m)
export const CANOPY_LEVELS = 3;           // levels 0..2 (2 / 4 / 8 m texels, 512 / 1024 / 2048 m tiles)
export const SPLIT_K = 300;               // a level-c tile splits nearer than SPLIT_K × its texel (m)
export const levelTexel = (c) => 2 * 2 ** c;
export const levelTile = (c) => 512 * 2 ** c;

// == bake.py LIGHT_CROWN / LIGHT_SILHOUETTE (check-forest pins the twin):
// [profile, crown base / tree height, the sun share one crown removes]
export const SILHOUETTE = 0.85;
export const CROWN_PROFILE = {
  conifer: ['cone', 0.15, 0.85], larch: ['cone', 0.15, 0.70], columnar: ['cone', 0.15, 0.85],
  birch: ['dome', 0.35, 0.70], broadleaf: ['dome', 0.35, 0.85], spreading: ['dome', 0.35, 0.85],
  umbrella: ['dome', 0.50, 0.85], palm: ['dome', 0.60, 0.60],
};
// snow the form's crown catches (0..1; the draw applies the snow, §5)
export const SNOW_CATCH = { conifer: 1.0, columnar: 0.9, larch: 0.7, broadleaf: 0.55, birch: 0.5, spreading: 0.6, umbrella: 0.6, palm: 0.3 };
export const BARE_R = 0.6;                // a bare crown keeps 1 − BARE_R of its radius (the branch skeleton)
export const BARE_CATCH = 0.7;            // ... and 1 − BARE_CATCH of its catch
export const BARK = [0.085, 0.07, 0.055]; // linear: the bare branch colour
// the light (shadeRef): the self-shadow march, the openness taps
export const SELF_M = 24, SUN_STEPS = 12, SUN_EPS = 0.25;
export const AO_R = [4, 12], AO_RT = [1, 2], AO_DIRS = 8, AO_EXCESS = 0.5, INNER_AO = 0.35;
// per-crown variation from the position hash (crownpack.js crownHash)
export const VAR_K = [0.86, 0.28], VAR_WARM = 0.08;
// the light numbers of a family (shadeRef; canopygen.js inlines them in its shade program)
export const TREE_LIGHT = { selfM: SELF_M, sunSteps: SUN_STEPS, sunEps: SUN_EPS, aoR: AO_R, aoRT: AO_RT, catchDepth: 0.5 };

// ---- the shrub family's numbers
export const SHRUB_LEVELS = 2;            // levels 0..1 (1 / 2 m texels, 256 / 512 m tiles)
export const shrubTexel = (c) => 2 ** c;
export const shrubTile = (c) => 256 * 2 ** c;
export const SHRUB_TOP_M = 3;             // == bake.py CROWN_MIN_H: the shrub band's top (the bake clips h to it)
// == bake.py LIGHT_SHRUB / LIGHT_SHRUB_R (check-forest-shrub pins the twin):
// [profile, base / height, the sun share one shrub removes], radius / height
export const SHRUB_PROFILE = ['dome', 0.0, 0.85];
export const SHRUB_R = 0.8 * SILHOUETTE;  // 0.68: a 1.6 h wide quad's silhouette half-width
export const SHRUB_REACH_M = 2.5;         // the widest drawn shrub radius (0.68 × 3 = 2.04 m)
export const SHRUB_CAP_M = 2400;          // == grass.js SHRUB_CAP_DEFAULT_M (no shrub drawn on ground above the cap)
// == shrubs.js's defaults (no meta.vegetation.shrubs)
export const SHRUB_STYLE = { color: '#3e6234', bloom: '#c95a74', needle: 0.45 };
// the painted bush atlas (vegpaint.js paintBushCanvas): the mean shade k its
// columns multiply the sRGB colour by — leafy blobs 0.97, needle strokes 0.70
export const SHRUB_K = [0.97, 0.70];
// snow per style: leafy = the broadleaf's catch, needle = the conifer's (the near shrubs' columns)
export const SHRUB_CATCH = [SNOW_CATCH.broadleaf, SNOW_CATCH.conifer];
// sun march 8 m / 8 steps (1 m: the level-0 texel; a 3 m shrub's shadow at a 20° sun), openness taps 2 / 6 m
export const SHRUB_LIGHT = { selfM: 8, sunSteps: 8, sunEps: 0.1, aoR: [2, 6], aoRT: [1, 2], catchDepth: 0 };

// THE FAMILIES: id (GenPass + pool), pack kind, levels, texel / tile of a
// level, the ground-index node a level-c tile is (128·2^k m), the finest
// ground-tree node its ground stage draws, the crown reach, the split factor
// (a level-c tile splits nearer than split × its texel), the light
export const FAMILY = {
  tree: { id: 'canopy', kind: 0, levels: CANOPY_LEVELS, texel: levelTexel, tile: levelTile, node: (c) => c + 2,
    groundMinK: (c) => c, reach: REACH_M, split: SPLIT_K, light: TREE_LIGHT },
  shrub: { id: 'shrub', kind: 1, levels: SHRUB_LEVELS, texel: shrubTexel, tile: shrubTile, node: (c) => c + 1,
    groundMinK: (c) => c - 1, reach: SHRUB_REACH_M, split: SPLIT_K, light: SHRUB_LIGHT },
};

const clamp = (x, a, b) => Math.min(b, Math.max(a, x));
const smooth = (a, b, x) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
const lin = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
export const hexLin = (hex) => [1, 3, 5].map((k) => lin(parseInt(hex.slice(k, k + 2), 16) / 255));
const HEXRE = /^#[0-9a-f]{6}$/i;
// GLSL packSnorm2x16 / the u16 crown depth, as the GPU stores them
export const snorm16 = (v) => Math.round(clamp(v, -1, 1) * 32767) / 32767;
export const unorm16 = (v) => Math.floor(clamp(v, 0, 1) * 65535 + 0.5) / 65535;

// the world's species model for the canopy: meta.vegetation + meta.trees.appear_hist
// + the world's SEA1 (sea: the species' own season ramps by name, season.js
// seaRamp; none given = every species on its form's ramp — never the
// vegetation block's) → {species (resolveSpecies, ≤ 4), lut (Uint8Array 255 |
// null), major, treeline, ramps (per species: its own ramp | null)}
export function speciesCtx(meta, sea = null) {
  const veg = (meta && meta.vegetation) || {};
  const species = resolveSpecies(veg);
  if (!species.length) throw new Error('canopy: meta.vegetation.species missing');
  const hist = meta && meta.trees && meta.trees.appear_hist;
  const lut = Array.isArray(hist) ? speciesLut(Uint32Array.from(hist), species) : null;
  let major = 0;
  species.forEach((s, i) => { if (s.share > species[major].share) major = i; });
  const treeline = Number(veg.treeline_m) || Number(meta && meta.bands && meta.bands.treeline_m) || 2250;
  const ramps = (veg.species || []).slice(0, 4).map((s) => (sea ? seaRamp(sea, s && s.name) : null));
  return { species, lut, major, treeline, ramps };
}

// per species at day-of-year doy → the uniforms the pass and the reference
// share: form numbers (hScale, w, base, dome), season (bare), colour (linear,
// tinted, bare-mixed), opacity, snow catch
export function seasonSpecies(ctx, doy) {
  return ctx.species.map((s, k) => {
    const fc = FORM_CFG[s.form] || FORM_CFG.conifer;
    const cp = CROWN_PROFILE[s.form] || CROWN_PROFILE.conifer;
    const se = formSeason(s.form, doy, ctx.ramps[k] || null);
    const base = hexLin(s.color);
    const bare = clamp(se.bare, 0, 1);
    const col = base.map((v, j) => clamp(v * se.tint[j], 0, 1) * (1 - bare) + BARK[j] * bare);
    return { form: s.form, hScale: fc.hScale, w: fc.w, base: cp[1], dome: cp[0] === 'dome' ? 1 : 0, opacity: cp[2],
      bare, rk: 1 - BARE_R * bare, col, catch: (SNOW_CATCH[s.form] ?? SNOW_CATCH.conifer) * (1 - BARE_CATCH * bare) };
  });
}

// the near sprite's season (gs-sprite-snow; vegmat.js patchSpriteMat, the
// sprite twin of seasonSpecies): own = the species' own ramp (SEA1, season.js
// seaRamp) or null → the form's FORM_SEASON. tint multiplies the foliage
// texels, bare drops them (season.js SPR_SEASON_GLSL), catch is the canopy
// tile's — so near and far hold the same snow.
export function spriteSeason(form, doy, own) {
  const se = formSeason(form, doy, Array.isArray(own) ? own : null);
  const bare = clamp(se.bare, 0, 1);
  return { tint: se.tint, bare, catch: (SNOW_CATCH[form] ?? SNOW_CATCH.conifer) * (1 - BARE_CATCH * bare) };
}

// the work frame of tile (c, i, j): texel t m, tile origin X0 / Z0, frame
// origin x0 / z0 (the halo's corner), y0 the tile's height floor, the
// treeline (speciesCtx) relative to it
export function canopyFrame(c, i, j, y0, treeline = 2250) {
  const t = levelTexel(c), T = levelTile(c);
  return { c, i, j, t, tileM: T, X0: i * T, Z0: j * T, x0: i * T - HALO * t, z0: j * T - HALO * t, n: FRAME_N, y0,
    treelineRel: treeline - y0 };
}

// the shrub family's frame: capRel = the shrub cap relative to y0 (none drawn on ground above it)
export function shrubFrame(c, i, j, y0, cap = SHRUB_CAP_M) {
  const t = shrubTexel(c), T = shrubTile(c);
  return { c, i, j, t, tileM: T, X0: i * T, Z0: j * T, x0: i * T - HALO * t, z0: j * T - HALO * t, n: FRAME_N, y0,
    capRel: cap - y0 };
}

// the crown packs (tile_m, list [[i, j, n]]) a frame reads: tile + reach
// (REACH_M trees, SHRUB_REACH_M shrubs)
export function packsFor(frame, crowns, reach = REACH_M) {
  const P = crowns.tile_m, out = [];
  const a = frame.X0 - reach, b = frame.X0 + frame.tileM + reach;
  const c = frame.Z0 - reach, d = frame.Z0 + frame.tileM + reach;
  for (const [i, j, n] of crowns.list) {
    if (!(n > 0)) continue;
    if ((i + 1) * P <= a || i * P >= b || (j + 1) * P <= c || j * P >= d) continue;
    out.push([i, j]);
  }
  return out.sort((p, q) => p[1] - q[1] || p[0] - q[0]);
}

// decoded packs (crownpack.js decodeCrowns) → the frame's crowns in draw
// order (packs by j then i, records in Morton order — the tie order of the
// depth test): frame-local x / z (m), h byte, r byte, species, hash.
// Steps: yields after each pack and every 4096 records (canopygen's prep
// splits the CPU across runs); returns what gatherCrowns returns
export function* gatherCrownsSteps(decoded, frame, ctx) {
  return yield* gatherSteps(decoded, frame, ctx, 0, REACH_M);
}
export function gatherCrowns(decoded, frame, ctx) {
  return drain(gatherCrownsSteps(decoded, frame, ctx));
}
// the shrub family's gather (shrub packs, SHRUB_REACH_M): r 0, sp = the
// style (shrubNeedle: 1 needle, 0 leafy); ctx = shrubCtx
export function* gatherShrubsSteps(decoded, frame, ctx) {
  return yield* gatherSteps(decoded, frame, ctx, 1, SHRUB_REACH_M);
}
export function gatherShrubs(decoded, frame, ctx) {
  return drain(gatherShrubsSteps(decoded, frame, ctx));
}
// crownpack.js decodeCrowns as a generator: yields every `every` records
// (canopygen's prep, within the gen slice: gs-canopygen-slice) and returns the
// same object, byte for byte (check-forest "stepped decode" pins it). A twin
// HERE, not in crownpack.js: crownpack is in the ground painter's key closure
// (engine/steps/_prepaint/painter.mjs PAINTER → groundwork.js), an edit there
// key-misses every shipped pre-painted slot
export function* decodeCrownsSteps(buf, every = 4096) {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const hd = readCrownHeader(u8);
  const { n, kind, bitsXZ, i, j, quantum } = hd;
  const qx = new Int32Array(n), qz = new Int32Array(n);
  const x = new Float32Array(n), z = new Float32Array(n), hash = new Uint32Array(n);
  const ox = i << bitsXZ, oz = j << bitsXZ, kmax = 2 ** (2 * bitsXZ);
  let p = CPK_HEADER, key = 0;
  for (let k = 0; k < n; k++) {
    if (k && k % every === 0) yield;
    let d = 0, mul = 1, b;
    do {
      if (p >= u8.length) throw new Error('crownpack: truncated key deltas');
      b = u8[p++];
      d += (b & 0x7f) * mul;
      mul *= 128;
    } while (b & 0x80);
    key += d;
    if (key >= kmax) throw new Error(`crownpack: key ${key} outside the tile`);
    const ax = ox + unspread(key), az = oz + unspread(key >>> 1);
    qx[k] = ax; qz[k] = az;
    x[k] = (ax + 0.5) * quantum; z[k] = (az + 0.5) * quantum;
    hash[k] = crownHash(ax, az);
  }
  const planes = kind === 0 ? 3 : 2;
  if (u8.length - p !== planes * n) throw new Error(`crownpack: body is ${u8.length - p} B after the keys, expected ${planes * n}`);
  const plane = () => { const a = u8.slice(p, p + n); p += n; return a; };
  const h = plane();
  const r = kind === 0 ? plane() : null;
  const appear = plane();
  return { ...hd, qx, qz, x, z, h, r, appear, hash };
}
// crownpack.js unspread (the Morton key's odd / even bits)
function unspread(v) {
  v &= 0x55555555;
  v = (v | (v >>> 1)) & 0x33333333;
  v = (v | (v >>> 2)) & 0x0f0f0f0f;
  v = (v | (v >>> 4)) & 0x00ff00ff;
  return (v | (v >>> 8)) & 0x0000ffff;
}
function drain(g) {
  let r;
  while (!(r = g.next()).done);
  return r.value;
}
function* gatherSteps(decoded, frame, ctx, kind, reach) {
  const ps = [...decoded].sort((p, q) => p.j - q.j || p.i - q.i);
  const ext = frame.n * frame.t;
  let n = 0;
  for (const p of ps) n += p.n;
  const lx = new Float64Array(n), lz = new Float64Array(n), h = new Uint8Array(n), r = new Uint8Array(n);
  const sp = new Uint8Array(n), hash = new Uint32Array(n);
  let k = 0;
  for (const p of ps) {
    if (p.kind !== kind) throw new Error(kind ? 'canopy: a tree pack in the shrub layer' : 'canopy: a shrub pack in the tree layer');
    for (let m = 0; m < p.n; m++) {
      if (m && !(m & 4095)) yield;
      const x = p.x[m] - frame.x0, z = p.z[m] - frame.z0;
      if (x < -reach || z < -reach || x > ext + reach || z > ext + reach) continue;
      lx[k] = x; lz[k] = z; h[k] = p.h[m]; hash[k] = p.hash[m];
      if (kind) {
        sp[k] = shrubNeedle(p.hash[m] >>> 12, ctx.needle) ? 1 : 0;
      } else {
        r[k] = p.r[m];
        const a = p.appear[m];
        sp[k] = ctx.lut && a !== 255 ? ctx.lut[a] : ctx.major;
      }
      k++;
    }
    yield;
  }
  return { n: k, lx: lx.subarray(0, k), lz: lz.subarray(0, k), h: h.subarray(0, k), r: r.subarray(0, k),
    sp: sp.subarray(0, k), hash: hash.subarray(0, k) };
}

// ---- the shrub style: ONE rule for the near shrubs (shrubs.js) and the
// shrub canopy tile. shrubStyle(veg.shrubs, prev) = shrubs.js _style: a
// colour / bloom only when a #rrggbb, needle_share clamped 0..1 (else prev's)
export function shrubStyle(sh, prev = SHRUB_STYLE) {
  if (!sh) return { ...prev };
  return {
    color: HEXRE.test(sh.color || '') ? sh.color : prev.color,
    bloom: HEXRE.test(sh.bloom || '') ? sh.bloom : null,
    needle: Number.isFinite(sh.needle_share) ? Math.min(1, Math.max(0, sh.needle_share)) : prev.needle,
  };
}
// leafy vs needle per shrub: key = crownHash >>> 12 (the near shrubs' key),
// hashed so mixed slopes read mottled, not banded; true = the needle column
export function shrubNeedle(key, needle) {
  return ((key * 2654435761 >>> 9) % 1000) / 1000 < needle;
}
// the world's shrub model: {style, needle, cap (m: no shrub drawn on ground above)}
export function shrubCtx(meta) {
  const sh = meta && meta.vegetation && meta.vegetation.shrubs;
  const style = shrubStyle(sh || null);
  const m = sh && sh.max_elev_m;
  return { style, needle: style.needle, cap: Number.isFinite(m) && m > 0 ? m : SHRUB_CAP_M };
}
// the two styles as the pass's per-"species" uniforms (seasonSpecies' shape;
// no season): colour = the atlas column's mean shade × the style colour
// (sRGB, then linear), opacity + catch per style
export function shrubStyles(ctx) {
  const c = [1, 3, 5].map((k) => parseInt(ctx.style.color.slice(k, k + 2), 16) / 255);
  return ['leafy', 'needle'].map((form, k) => ({ form, hScale: 1, w: 0, base: SHRUB_PROFILE[1], dome: 1, opacity: SHRUB_PROFILE[2],
    bare: 0, rk: 1, col: c.map((v) => lin(Math.min(1, v * SHRUB_K[k]))), catch: SHRUB_CATCH[k] }));
}

// one crown on ground yb (rel m): {cx, cz, R, top, base, dome} (rel m), null = not drawn
export function crownGeom(C, k, yb, spc, frame) {
  const s0 = spc[C.sp[k]];
  let s = (C.h[k] / 8) * s0.hScale;
  const rm = C.r[k] * 0.2;
  if (C.r[k] === 0) s *= 0.45 + 0.55 * clamp((frame.treelineRel - yb) / 140, 0, 1);
  const wf = C.r[k] > 0 ? clamp((2.35 * rm) / Math.max(1, s), 0.28, 1.15) : s0.w;
  const R = 0.5 * SILHOUETTE * s * wf * s0.rk;
  if (!(R > 0)) return null;
  return { cx: C.lx[k], cz: C.lz[k], R, top: yb + s, base: yb + s * s0.base, dome: s0.dome };
}

// one shrub on ground yb (rel m): the bake's dome (top h ≤ SHRUB_TOP_M,
// base SHRUB_PROFILE[1], radius SHRUB_R × h); null on ground above the cap
export function shrubGeom(C, k, yb, spc, frame) {
  if (yb > frame.capRel) return null;
  const s = Math.min(C.h[k] / CPK_H_PER_M[1], SHRUB_TOP_M);
  const R = SHRUB_R * s;
  if (!(R > 0)) return null;
  return { cx: C.lx[k], cz: C.lz[k], R, top: yb + s, base: yb + s * SHRUB_PROFILE[1], dome: 1 };
}

// the ground under a crown: the frame texel holding its centre (clamped)
export function groundAtCentre(ground, frame, x, z) {
  const n = frame.n;
  const i = clamp(Math.floor(x / frame.t), 0, n - 1), j = clamp(Math.floor(z / frame.t), 0, n - 1);
  return ground[j * n + i];
}

// SPLAT: ground (Float32Array n², rel m — the drawn ground rasterised) +
// the crowns → per frame texel Z (the max surface, rel m), flag (1 crown),
// species, hash, the crown-local normal (nx, nz; snorm16), crown depth
// (0 top .. 1 base; unorm16). The depth test: a crown takes a texel only
// strictly above what holds it (ground or an earlier crown). geom: crownGeom
// (trees) | shrubGeom (shrubs).
export function splatRef(C, ground, frame, spc, geom = crownGeom) {
  const n = frame.n, N = n * n, t = frame.t;
  const Z = Float64Array.from(ground), flag = new Uint8Array(N), sp = new Uint8Array(N), hash = new Uint32Array(N);
  const nx = new Float32Array(N), nz = new Float32Array(N), rel = new Float32Array(N);
  for (let k = 0; k < C.n; k++) {
    const g = geom(C, k, groundAtCentre(ground, frame, C.lx[k], C.lz[k]), spc, frame);
    if (!g) continue;
    const a = g.top - g.base;
    const i0 = Math.max(0, Math.floor((g.cx - g.R) / t)), i1 = Math.min(n - 1, Math.floor((g.cx + g.R) / t));
    const j0 = Math.max(0, Math.floor((g.cz - g.R) / t)), j1 = Math.min(n - 1, Math.floor((g.cz + g.R) / t));
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const dx = (i + 0.5) * t - g.cx, dz = (j + 0.5) * t - g.cz;
        const rho = Math.hypot(dx, dz), q = rho / g.R;
        if (q >= 1) continue;
        const sq = Math.sqrt(1 - q * q);
        const zc = g.dome ? 0.5 * (g.top + g.base) + 0.5 * a * sq : g.top - a * q;
        const p = j * n + i;
        if (!(zc > Z[p])) continue;
        Z[p] = zc; flag[p] = 1; sp[p] = C.sp[k]; hash[p] = C.hash[k];
        const sl = g.dome ? (0.5 * a * q) / (g.R * Math.max(sq, 0.05)) : a / g.R;
        let ux = 0, uz = 0;
        if (rho > 1e-6) { ux = (sl * dx) / rho; uz = (sl * dz) / rho; }
        const l = Math.hypot(ux, 1, uz);
        nx[p] = snorm16(ux / l); nz[p] = snorm16(uz / l);
        rel[p] = unorm16(a > 1e-3 ? (g.top - zc) / a : 0);
      }
    }
  }
  return { n, Z, flag, sp, hash, nx, nz, rel };
}

// RESOLVE: the tile's 256² R16UI heights (0 = no canopy)
export function resolveRef(S, frame) {
  const N = CANOPY_N, H = new Uint16Array(N * N);
  for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
    const p = (j + HALO) * S.n + i + HALO;
    if (S.flag[p]) H[j * N + i] = clamp(Math.floor(S.Z[p] * H_Q + 0.5), 1, 65535);
  }
  return H;
}

// the MAX-MIPMAP chain (Tevs et al. 2008): level l = the max of its 2×2
export function maxMips(H, n = CANOPY_N) {
  const out = [H];
  for (let s = n >> 1; s >= 1; s >>= 1) {
    const P = out[out.length - 1], d = s * 2, M = new Uint16Array(s * s);
    for (let j = 0; j < s; j++) for (let i = 0; i < s; i++) {
      const a = 2 * j * d + 2 * i;
      M[j * s + i] = Math.max(P[a], P[a + 1], P[a + d], P[a + d + 1]);
    }
    out.push(M);
  }
  return out;
}

// the openness taps of a level (m, frame axes): AO_DIRS per radius, the
// second ring turned half a step — the GPU gets the same numbers as uniforms;
// P = the family's light (TREE_LIGHT / SHRUB_LIGHT)
export function aoTaps(t, P = TREE_LIGHT) {
  const out = [];
  for (let r = 0; r < 2; r++) {
    const R = Math.max(P.aoR[r], P.aoRT[r] * t);
    for (let d = 0; d < AO_DIRS; d++) {
      const an = (2 * Math.PI * (d + 0.5 * r)) / AO_DIRS;
      out.push([R * Math.cos(an), R * Math.sin(an)]);
    }
  }
  return out;
}

// the snow a texel catches: form catch × the upward normal × crown depth
// (depth: the family's catchDepth — 0.5 trees, 0 shrubs: the normal only)
export function snowCatch(ck, ny, rel, depth = 0.5) {
  return clamp(ck * smooth(0.3, 0.9, ny) * (1 - depth * rel), 0, 1);
}

// the terrain's sun / sky visibility at a frame position (x, z frame m) —
// the light's inputs from outside the crowns: SHD2 vis_opaque / sky_opaque
// at the canopy texel (canopygen.js binds the samplers); the reference takes
// functions (SUNVIS_ONE: 1, no terrain occlusion)
export const SUNVIS_ONE = () => 1;
// a frame-aligned u8 grid (n², the SHD2 level at the frame texel) as such a
// function: the nearest texel (exact at the texel centres the pass samples)
export function frameVis(a, frame) {
  const n = frame.n, t = frame.t;
  return (x, z) => a[Math.min(n - 1, Math.max(0, Math.floor(z / t))) * n + Math.min(n - 1, Math.max(0, Math.floor(x / t)))] / 255;
}

// SHADE: per tile texel → A (linear albedo × E / W, coverage) and B (sky
// visibility, sun visibility, snow catch, species) as floats 0..1.
// light = meta.tex.light {sky, ground, sun, white}; L = toward the sun
// (scenelight.js sunDirFromLighting: game axes); sunVis / skyVis(xFrame,
// zFrame): the terrain's (SHD2) — skyVis multiplies the crown openness (E's
// sky term and B.r), a non-crown texel's B.r = skyVis. P = the family's
// light numbers (TREE_LIGHT / SHRUB_LIGHT).
export function shadeRef(S, frame, spc, light, L, sunVis = SUNVIS_ONE, skyVis = SUNVIS_ONE, P = TREE_LIGHT) {
  const N = CANOPY_N, n = S.n, t = frame.t;
  const A = new Float32Array(N * N * 4), B = new Float32Array(N * N * 4);
  const lh = Math.hypot(L[0], L[2]), dx = lh > 1e-6 ? L[0] / lh : 0, dz = lh > 1e-6 ? L[2] / lh : 0;
  const tanAlt = lh > 1e-6 ? L[1] / lh : 1e6;
  const taps = aoTaps(t, P);
  const at = (x, z) => {
    const i = Math.floor(x / t), j = Math.floor(z / t);
    return i < 0 || j < 0 || i >= n || j >= n ? -1 : j * n + i;
  };
  for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
    const p = (j + HALO) * n + i + HALO, o = 4 * (j * N + i);
    const px = (i + HALO + 0.5) * t, pz = (j + HALO + 0.5) * t;
    const tsv = sunVis(px, pz);
    const tsky = skyVis(px, pz);
    if (!S.flag[p]) { B[o] = tsky; B[o + 1] = tsv; B[o + 2] = 0; B[o + 3] = 1; continue; }
    const z = S.Z[p], s0 = spc[S.sp[p]];
    const nX = S.nx[p], nZ = S.nz[p], nY = Math.sqrt(Math.max(0, 1 - nX * nX - nZ * nZ));
    // self shadow: the first crown texel above the sun ray within selfM
    let vis = 1;
    for (let k = 1; k <= P.sunSteps; k++) {
      const s = (k * P.selfM) / P.sunSteps, q = at(px + s * dx, pz + s * dz);
      if (q >= 0 && S.flag[q] && S.Z[q] > z + s * tanAlt + P.sunEps) { vis = 1 - spc[S.sp[q]].opacity; break; }
    }
    // sky visibility: the openness over the frame's surface, then the crown's depth
    let open = 0;
    for (let r = 0; r < 2; r++) {
      const R = Math.max(P.aoR[r], P.aoRT[r] * t);
      for (let d = 0; d < AO_DIRS; d++) {
        const [ox, oz] = taps[r * AO_DIRS + d], q = at(px + ox, pz + oz);
        const e = q >= 0 ? Math.max(0, S.Z[q] - z) * AO_EXCESS / R : 0;
        open += 1 / (1 + e * e);
      }
    }
    const sky = (open / (2 * AO_DIRS)) * (1 - INNER_AO * S.rel[p]) * tsky;
    const nl = Math.max(0, nX * L[0] + nY * L[1] + nZ * L[2]);
    const w = 0.5 * nY + 0.5, sun = nl * vis * tsv;
    const h = S.hash[p], u1 = ((h >>> 8) & 255) / 255, u2 = ((h >>> 16) & 255) / 255;
    const k = VAR_K[0] + VAR_K[1] * u1, warm = (u2 - 0.5) * VAR_WARM;
    const vk = [k * (1 + warm), k, k * (1 - warm)];
    for (let c = 0; c < 3; c++) {
      const E = sky * (light.ground[c] + (light.sky[c] - light.ground[c]) * w) + light.sun[c] * sun;
      A[o + c] = clamp((clamp(s0.col[c] * vk[c], 0, 1) * E) / light.white, 0, 1);
    }
    A[o + 3] = 1;
    B[o] = sky; B[o + 1] = vis * tsv; B[o + 2] = snowCatch(s0.catch, nY, S.rel[p], P.catchDepth); B[o + 3] = S.sp[p] / 255;
  }
  return { A, B };
}

// the whole tile on the CPU: decoded packs + the frame's ground (rel m) →
// {S, H (mips), A, B}; frame from canopyFrame.
export function canopyRef(decoded, ground, frame, ctx, doy, light, L, sunVis, skyVis) {
  const spc = seasonSpecies(ctx, doy);
  const C = gatherCrowns(decoded, frame, ctx);
  const S = splatRef(C, ground, frame, spc);
  const H = maxMips(resolveRef(S, frame));
  return { C, S, H, ...shadeRef(S, frame, spc, light, L, sunVis, skyVis) };
}

// the shrub tile on the CPU (the shrub family's canopyRef): decoded shrub
// packs + the frame's ground (rel m) → {C, S, H (mips), A, B}; frame from
// shrubFrame, ctx from shrubCtx
export function shrubRef(decoded, ground, frame, ctx, light, L, sunVis, skyVis) {
  const spc = shrubStyles(ctx);
  const C = gatherShrubs(decoded, frame, ctx);
  const S = splatRef(C, ground, frame, spc, shrubGeom);
  const H = maxMips(resolveRef(S, frame));
  return { C, S, H, ...shadeRef(S, frame, spc, light, L, sunVis, skyVis, SHRUB_LIGHT) };
}

// THE TILE SELECTION of a family at pos (canopygen.js tick; check-forest-
// shrub sweeps it): a quadtree from level hi down to lo — a level-c tile
// splits nearer than fam.split (SPLIT_K) × its texel — whose nearest point lies within
// range, not wholly inside vm (its farthest point nearer: the near sprites'
// / shrubs', and so is every child), only where hasNode(c, i, j) (the ground
// index has the tile's node) and packsOf(c, i, j) is non-empty (a pack
// reaches it). → [{id 'c_i_j', c, i, j, rank: the nearest distance, packs}]
export function familyTiles(fam, pos, { lo, hi, range, vm, hasNode, packsOf }) {
  const out = [];
  const sel = (c, i, j) => {
    const T = fam.tile(c);
    const dx = Math.max(i * T - pos.x, 0, pos.x - (i + 1) * T), dz = Math.max(j * T - pos.z, 0, pos.z - (j + 1) * T);
    const d = Math.hypot(dx, dz);
    if (d > range || !hasNode(c, i, j)) return;
    const fx = Math.max(Math.abs(pos.x - i * T), Math.abs(pos.x - (i + 1) * T)), fz = Math.max(Math.abs(pos.z - j * T), Math.abs(pos.z - (j + 1) * T));
    if (Math.hypot(fx, fz) < vm) return;
    const packs = packsOf(c, i, j);
    if (!packs.length) return;
    if (c > lo && d < fam.split * fam.texel(c)) {
      for (let k = 0; k < 4; k++) sel(c - 1, 2 * i + (k & 1), 2 * j + (k >> 1));
      return;
    }
    out.push({ id: `${c}_${i}_${j}`, c, i, j, rank: d, packs });
  };
  const T = fam.tile(hi);
  for (let j = Math.floor((pos.z - range) / T); j <= Math.floor((pos.z + range) / T); j++) {
    for (let i = Math.floor((pos.x - range) / T); i <= Math.floor((pos.x + range) / T); i++) sel(hi, i, j);
  }
  return out;
}
